import React, { useState, useEffect, useRef } from 'react';

/* ─────────────────────────────────────────────────────────────────
   Inline-editable text — click to edit, blur/Enter to commit
   ───────────────────────────────────────────────────────────────── */
const EditableText = ({ value, onChange, placeholder, multiline = false, className = '', inputClassName = '' }) => {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft]   = useState(value || '');
  const ref = useRef(null);

  useEffect(() => { setDraft(value || ''); }, [value]);
  useEffect(() => { if (editing && ref.current) ref.current.focus(); }, [editing]);

  const commit = () => { setEditing(false); onChange(draft); };

  if (!editing) {
    return (
      <div
        className={`group cursor-text rounded-md px-2 py-1 -mx-2 transition-all hover:bg-blue-50/60 border border-transparent hover:border-blue-200 ${className}`}
        onClick={() => setEditing(true)}
        title="Click to edit"
      >
        {value || <span className="text-slate-300 italic text-[12px]">{placeholder || 'Click to add…'}</span>}
        <span className="msi text-[12px] text-blue-300 opacity-0 group-hover:opacity-100 ml-1 transition-opacity align-middle">edit</span>
      </div>
    );
  }

  if (multiline) {
    return (
      <textarea
        ref={ref}
        value={draft}
        onChange={e => setDraft(e.target.value)}
        onBlur={commit}
        onKeyDown={e => { if (e.key === 'Escape') { setDraft(value || ''); setEditing(false); } }}
        className={`w-full rounded-lg border border-blue-300 bg-white px-3 py-2 text-[13px] leading-relaxed outline-none focus:ring-2 focus:ring-blue-200 resize-y min-h-[80px] font-inter ${inputClassName}`}
        placeholder={placeholder}
      />
    );
  }

  return (
    <input
      ref={ref}
      type="text"
      value={draft}
      onChange={e => setDraft(e.target.value)}
      onBlur={commit}
      onKeyDown={e => { if (e.key === 'Enter') commit(); if (e.key === 'Escape') { setDraft(value || ''); setEditing(false); } }}
      className={`w-full rounded-lg border border-blue-300 bg-white px-3 py-1.5 text-[13px] outline-none focus:ring-2 focus:ring-blue-200 font-inter ${inputClassName}`}
      placeholder={placeholder}
    />
  );
};

/* ─────────────────────────────────────────────────────────────────
   Collapsible Section wrapper
   ───────────────────────────────────────────────────────────────── */
const Section = ({ title, icon, count, children, defaultOpen = true }) => {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="border border-slate-200 rounded-xl bg-white shadow-sm overflow-hidden">
      <button
        onClick={() => setOpen(o => !o)}
        className="w-full flex items-center gap-2.5 px-5 py-3.5 bg-slate-50 hover:bg-slate-100 transition-colors text-left"
      >
        <span className="msi text-blue text-[18px]">{icon}</span>
        <span className="text-[13px] font-bold text-navy font-montserrat flex-1">{title}</span>
        {count !== undefined && <span className="text-[10px] font-bold bg-blue/10 text-blue px-2 py-0.5 rounded-full">{count}</span>}
        <span className={`msi text-slate-400 text-[18px] transition-transform ${open ? 'rotate-180' : ''}`}>expand_more</span>
      </button>
      {open && <div className="px-5 py-4 space-y-3">{children}</div>}
    </div>
  );
};

/* ─────────────────────────────────────────────────────────────────
   Bullet list editor
   ───────────────────────────────────────────────────────────────── */
const BulletEditor = ({ bullets = [], onChange }) => {
  const addBullet = () => onChange([...bullets, '']);
  const updateBullet = (idx, val) => { const b = [...bullets]; b[idx] = val; onChange(b); };
  const removeBullet = (idx) => onChange(bullets.filter((_, i) => i !== idx));
  const moveBullet = (idx, dir) => {
    const b = [...bullets];
    const swap = idx + dir;
    if (swap < 0 || swap >= b.length) return;
    [b[idx], b[swap]] = [b[swap], b[idx]];
    onChange(b);
  };

  return (
    <div className="space-y-1.5">
      {bullets.map((bullet, idx) => (
        <div key={idx} className="flex items-start gap-1.5 group">
          <span className="text-slate-300 text-[12px] mt-2 shrink-0 font-mono w-4 text-right">{idx + 1}.</span>
          <div className="flex-1">
            <EditableText
              value={bullet}
              onChange={val => updateBullet(idx, val)}
              placeholder="Enter bullet point…"
              multiline
              className="text-[12.5px] text-slate-700 leading-relaxed"
            />
          </div>
          <div className="flex flex-col gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity shrink-0">
            <button onClick={() => moveBullet(idx, -1)} className="w-5 h-5 flex items-center justify-center rounded hover:bg-slate-100 text-slate-400" title="Move up"><span className="msi text-[14px]">arrow_upward</span></button>
            <button onClick={() => moveBullet(idx, 1)} className="w-5 h-5 flex items-center justify-center rounded hover:bg-slate-100 text-slate-400" title="Move down"><span className="msi text-[14px]">arrow_downward</span></button>
            <button onClick={() => removeBullet(idx)} className="w-5 h-5 flex items-center justify-center rounded hover:bg-red-50 text-red-400" title="Delete"><span className="msi text-[14px]">close</span></button>
          </div>
        </div>
      ))}
      <button onClick={addBullet} className="inline-flex items-center gap-1 text-[11px] font-semibold text-blue hover:text-blue/80 transition-colors mt-1">
        <span className="msi text-[14px]">add</span> Add bullet
      </button>
    </div>
  );
};

/* ─────────────────────────────────────────────────────────────────
   Experience item card
   ───────────────────────────────────────────────────────────────── */
const ExperienceCard = ({ exp, onChange, onRemove, index }) => (
  <div className="border border-slate-100 rounded-lg bg-slate-50/50 p-4 space-y-2">
    <div className="flex items-center justify-between gap-2">
      <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">Experience {index + 1}</span>
      <button onClick={onRemove} className="text-red-400 hover:text-red-600 transition-colors" title="Remove"><span className="msi text-[16px]">delete_outline</span></button>
    </div>
    <div className="grid grid-cols-2 gap-2">
      <div>
        <label className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider">Company</label>
        <EditableText value={exp.company} onChange={v => onChange({ ...exp, company: v })} placeholder="Company name" />
      </div>
      <div>
        <label className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider">Location</label>
        <EditableText value={exp.location} onChange={v => onChange({ ...exp, location: v })} placeholder="City" />
      </div>
      <div>
        <label className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider">Position</label>
        <EditableText value={exp.position} onChange={v => onChange({ ...exp, position: v })} placeholder="Job title" />
      </div>
      <div>
        <label className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider">Dates</label>
        <EditableText value={exp.dates || exp.duration} onChange={v => onChange({ ...exp, dates: v })} placeholder="MM/YYYY – MM/YYYY" />
      </div>
    </div>
    <div>
      <label className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider mb-1 block">Bullet Points</label>
      <BulletEditor bullets={exp.bullets || exp.responsibilities || []} onChange={b => onChange({ ...exp, bullets: b })} />
    </div>
  </div>
);

/* ─────────────────────────────────────────────────────────────────
   Education item card
   ───────────────────────────────────────────────────────────────── */
const EducationCard = ({ edu, onChange, onRemove, index }) => (
  <div className="border border-slate-100 rounded-lg bg-slate-50/50 p-4 space-y-2">
    <div className="flex items-center justify-between gap-2">
      <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">Education {index + 1}</span>
      <button onClick={onRemove} className="text-red-400 hover:text-red-600 transition-colors" title="Remove"><span className="msi text-[16px]">delete_outline</span></button>
    </div>
    <div className="grid grid-cols-2 gap-2">
      <div>
        <label className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider">Institution</label>
        <EditableText value={edu.institution || edu.university} onChange={v => onChange({ ...edu, institution: v })} placeholder="University name" />
      </div>
      <div>
        <label className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider">Location</label>
        <EditableText value={edu.location} onChange={v => onChange({ ...edu, location: v })} placeholder="City" />
      </div>
      <div>
        <label className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider">Degree</label>
        <EditableText value={edu.degree} onChange={v => onChange({ ...edu, degree: v })} placeholder="B.S. / M.S. / etc." />
      </div>
      <div>
        <label className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider">Date</label>
        <EditableText value={edu.graduationDate || edu.duration} onChange={v => onChange({ ...edu, graduationDate: v })} placeholder="YYYY" />
      </div>
    </div>
    <div>
      <label className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider">Details</label>
      <EditableText value={edu.details} onChange={v => onChange({ ...edu, details: v })} placeholder="GPA, honours, thesis…" multiline />
    </div>
  </div>
);

/* ─────────────────────────────────────────────────────────────────
   Skills editor — category : items
   ───────────────────────────────────────────────────────────────── */
const SkillsEditor = ({ skills, onChange }) => {
  // Normalize to array of {category, items}
  const normalize = (s) => {
    if (!s) return [];
    if (typeof s === 'string') return [{ category: '', items: s }];
    if (Array.isArray(s)) return s.map(item => {
      if (typeof item === 'string') return { category: '', items: item };
      return { category: item.category || '', items: Array.isArray(item.items) ? item.items.join(', ') : (item.items || '') };
    });
    return [];
  };

  const [rows, setRows] = useState(() => normalize(skills));
  useEffect(() => { setRows(normalize(skills)); }, [skills]);

  const commit = (newRows) => {
    setRows(newRows);
    onChange(newRows.map(r => ({ category: r.category, items: r.items })));
  };

  return (
    <div className="space-y-2">
      {rows.map((row, idx) => (
        <div key={idx} className="flex items-start gap-2 group">
          <div className="w-[35%]">
            <EditableText
              value={row.category}
              onChange={v => { const r = [...rows]; r[idx] = { ...r[idx], category: v }; commit(r); }}
              placeholder="Category"
              className="text-[12px] font-bold text-navy"
            />
          </div>
          <div className="flex-1">
            <EditableText
              value={row.items}
              onChange={v => { const r = [...rows]; r[idx] = { ...r[idx], items: v }; commit(r); }}
              placeholder="skill1, skill2, skill3"
              className="text-[12px] text-slate-600"
            />
          </div>
          <button onClick={() => commit(rows.filter((_, i) => i !== idx))} className="opacity-0 group-hover:opacity-100 text-red-400 hover:text-red-600 transition-all mt-1 shrink-0"><span className="msi text-[16px]">close</span></button>
        </div>
      ))}
      <button onClick={() => commit([...rows, { category: '', items: '' }])} className="inline-flex items-center gap-1 text-[11px] font-semibold text-blue hover:text-blue/80 transition-colors">
        <span className="msi text-[14px]">add</span> Add skill row
      </button>
    </div>
  );
};

/* ─────────────────────────────────────────────────────────────────
   Custom Section editor (Patents, Speaking, etc.)
   ───────────────────────────────────────────────────────────────── */
const CustomSectionEditor = ({ section, onChange }) => {
  const updateItem = (idx, item) => {
    const items = [...(section.items || [])];
    items[idx] = item;
    onChange({ ...section, items });
  };
  const removeItem = (idx) => onChange({ ...section, items: (section.items || []).filter((_, i) => i !== idx) });
  const addItem = () => onChange({ ...section, items: [...(section.items || []), { name: '', description: '', date: '' }] });

  return (
    <div className="space-y-3">
      <EditableText
        value={section.sectionTitle || section.title}
        onChange={v => onChange({ ...section, sectionTitle: v, title: v })}
        placeholder="Section title"
        className="text-[14px] font-bold text-navy"
      />
      {(section.items || []).map((item, idx) => (
        <div key={idx} className="border border-slate-100 rounded-lg bg-slate-50/50 p-3 space-y-2 group">
          <div className="flex items-center justify-between">
            <EditableText value={item.name || item.title} onChange={v => updateItem(idx, { ...item, name: v })} placeholder="Item name" className="text-[12px] font-semibold" />
            <button onClick={() => removeItem(idx)} className="opacity-0 group-hover:opacity-100 text-red-400 hover:text-red-600 transition-all"><span className="msi text-[14px]">close</span></button>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <EditableText value={item.issuer || item.organization} onChange={v => updateItem(idx, { ...item, issuer: v })} placeholder="Issuer / org" className="text-[11px] text-slate-500" />
            <EditableText value={item.date || item.dates} onChange={v => updateItem(idx, { ...item, date: v })} placeholder="Date" className="text-[11px] text-slate-500" />
          </div>
          {item.description !== undefined && (
            <EditableText value={item.description} onChange={v => updateItem(idx, { ...item, description: v })} placeholder="Description" multiline className="text-[11px] text-slate-600" />
          )}
          {(item.bullets?.length > 0 || item.description === undefined) && (
            <BulletEditor bullets={item.bullets || []} onChange={b => updateItem(idx, { ...item, bullets: b })} />
          )}
        </div>
      ))}
      <button onClick={addItem} className="inline-flex items-center gap-1 text-[11px] font-semibold text-blue hover:text-blue/80 transition-colors">
        <span className="msi text-[14px]">add</span> Add item
      </button>
    </div>
  );
};

/* ═════════════════════════════════════════════════════════════════
   MAIN ResumeEditor component
   ═════════════════════════════════════════════════════════════════ */
const ResumeEditor = ({ resumeData, onSave, onCancel, saving = false }) => {
  const [data, setData] = useState(() => JSON.parse(JSON.stringify(resumeData || {})));
  const [dirty, setDirty] = useState(false);

  // Deep-clone on mount to avoid mutating upstream state
  useEffect(() => {
    setData(JSON.parse(JSON.stringify(resumeData || {})));
    setDirty(false);
  }, [resumeData]);

  const update = (path, value) => {
    setData(prev => {
      const next = JSON.parse(JSON.stringify(prev));
      const keys = path.split('.');
      let obj = next;
      for (let i = 0; i < keys.length - 1; i++) obj = obj[keys[i]];
      obj[keys[keys.length - 1]] = value;
      return next;
    });
    setDirty(true);
  };

  const updateExp = (idx, exp) => {
    const list = [...(data.experience || [])];
    list[idx] = exp;
    update('experience', list);
  };
  const removeExp = (idx) => update('experience', (data.experience || []).filter((_, i) => i !== idx));
  const addExp = () => update('experience', [...(data.experience || []), { company: '', position: '', location: '', dates: '', bullets: [''] }]);

  const updateEdu = (idx, edu) => { const list = [...(data.education || [])]; list[idx] = edu; update('education', list); };
  const removeEdu = (idx) => update('education', (data.education || []).filter((_, i) => i !== idx));

  const updateCert = (idx, cert) => { const list = [...(data.certifications || [])]; list[idx] = cert; update('certifications', list); };
  const removeCert = (idx) => update('certifications', (data.certifications || []).filter((_, i) => i !== idx));

  const updateAward = (idx, award) => { const list = [...(data.awards || [])]; list[idx] = award; update('awards', list); };
  const removeAward = (idx) => update('awards', (data.awards || []).filter((_, i) => i !== idx));

  const updateCustom = (idx, sec) => { const list = [...(data.customSections || [])]; list[idx] = sec; update('customSections', list); };
  const removeCustom = (idx) => update('customSections', (data.customSections || []).filter((_, i) => i !== idx));

  return (
    <div className="fixed inset-0 bg-white z-[2000] flex flex-col animate-fade-in overflow-hidden">
      {/* Top bar */}
      <nav className="h-[72px] bg-navy border-b border-white/5 flex items-center justify-between px-8 shrink-0">
        <div className="flex items-center gap-4">
          <div className="w-10 h-10 bg-blue rounded-xl flex items-center justify-center"><span className="msi text-white text-[20px]">edit_note</span></div>
          <div>
            <h2 className="text-white font-montserrat font-bold text-lg leading-tight">Resume Editor</h2>
            <p className="text-slate-400 text-[11px] font-medium uppercase tracking-wider">Edit your resume data · Changes regenerate the PDF</p>
          </div>
        </div>
        <div className="flex items-center gap-4">
          {dirty && <span className="text-amber-300 text-[11px] font-semibold flex items-center gap-1"><span className="msi text-[14px]">circle</span> Unsaved changes</span>}
          <button onClick={onCancel} disabled={saving} className="px-6 h-11 border border-white/20 text-white font-bold rounded-xl hover:bg-white/10 transition-all text-[13px] disabled:opacity-50">Discard</button>
          <button
            onClick={() => onSave(data)}
            disabled={saving || !dirty}
            className="px-8 h-11 bg-premium-green text-white font-bold rounded-xl hover:bg-green-600 transition-all text-[13px] shadow-lg shadow-green-900/30 flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {saving ? (
              <><div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div> Regenerating…</>
            ) : (
              <><span className="msi text-[18px]">check_circle</span> Save & Regenerate PDF</>
            )}
          </button>
        </div>
      </nav>

      {/* Scrollable body */}
      <div className="flex-1 overflow-y-auto bg-slate-50 custom-scrollbar">
        <div className="max-w-[780px] mx-auto py-8 px-4 space-y-5">

          {/* Header info */}
          <Section title="Personal Info" icon="person" defaultOpen={true}>
            <div className="grid grid-cols-2 gap-3">
              <div className="col-span-2">
                <label className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider">Full Name</label>
                <EditableText value={data.name} onChange={v => update('name', v)} placeholder="Your full name" className="text-[16px] font-bold text-navy" />
              </div>
              <div>
                <label className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider">Email</label>
                <EditableText value={data.email} onChange={v => update('email', v)} placeholder="you@email.com" />
              </div>
              <div>
                <label className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider">Phone</label>
                <EditableText value={data.phone} onChange={v => update('phone', v)} placeholder="+1 (555) …" />
              </div>
              <div>
                <label className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider">Location</label>
                <EditableText value={data.location} onChange={v => update('location', v)} placeholder="City, State" />
              </div>
              <div>
                <label className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider">LinkedIn</label>
                <EditableText value={data.linkedin} onChange={v => update('linkedin', v)} placeholder="linkedin.com/in/…" />
              </div>
              <div>
                <label className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider">GitHub</label>
                <EditableText value={data.github} onChange={v => update('github', v)} placeholder="github.com/…" />
              </div>
              <div>
                <label className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider">Portfolio / Website</label>
                <EditableText value={data.website || data.portfolio} onChange={v => update('website', v)} placeholder="yoursite.com" />
              </div>
            </div>
          </Section>

          {/* Summary */}
          <Section title="Professional Summary" icon="subject" defaultOpen={true}>
            <EditableText
              value={data.summary}
              onChange={v => update('summary', v)}
              placeholder="Write a 3–4 sentence professional summary…"
              multiline
              className="text-[13px] text-slate-700 leading-relaxed"
            />
          </Section>

          {/* Experience */}
          <Section title="Work Experience" icon="work" count={data.experience?.length || 0} defaultOpen={true}>
            {(data.experience || []).map((exp, idx) => (
              <ExperienceCard key={idx} exp={exp} index={idx} onChange={e => updateExp(idx, e)} onRemove={() => removeExp(idx)} />
            ))}
            <button onClick={addExp} className="w-full border-2 border-dashed border-slate-200 rounded-lg py-3 text-[12px] font-semibold text-slate-400 hover:border-blue hover:text-blue transition-all flex items-center justify-center gap-1">
              <span className="msi text-[16px]">add</span> Add Experience
            </button>
          </Section>

          {/* Projects */}
          {(data.projects?.length > 0) && (
            <Section title="Projects" icon="folder_special" count={data.projects?.length || 0} defaultOpen={false}>
              {(data.projects || []).map((proj, idx) => (
                <div key={idx} className="border border-slate-100 rounded-lg bg-slate-50/50 p-4 space-y-2 group">
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">Project {idx + 1}</span>
                    <button onClick={() => update('projects', (data.projects || []).filter((_, i) => i !== idx))} className="text-red-400 hover:text-red-600 transition-colors" title="Remove"><span className="msi text-[16px]">delete_outline</span></button>
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <label className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider">Title</label>
                      <EditableText value={proj.title || proj.name} onChange={v => { const list = [...(data.projects||[])]; list[idx] = { ...proj, title: v, name: v }; update('projects', list); }} placeholder="Project name" />
                    </div>
                    <div>
                      <label className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider">Role / Subtitle</label>
                      <EditableText value={proj.role || proj.subtitle} onChange={v => { const list = [...(data.projects||[])]; list[idx] = { ...proj, role: v }; update('projects', list); }} placeholder="Role or subtitle" />
                    </div>
                    <div>
                      <label className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider">Dates</label>
                      <EditableText value={proj.dates || proj.date} onChange={v => { const list = [...(data.projects||[])]; list[idx] = { ...proj, dates: v }; update('projects', list); }} placeholder="Dates" />
                    </div>
                    <div>
                      <label className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider">Link</label>
                      <EditableText value={proj.link || proj.url} onChange={v => { const list = [...(data.projects||[])]; list[idx] = { ...proj, link: v }; update('projects', list); }} placeholder="https://…" />
                    </div>
                  </div>
                  <div>
                    <label className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider mb-1 block">Bullet Points</label>
                    <BulletEditor bullets={proj.bullets || proj.responsibilities || []} onChange={b => { const list = [...(data.projects||[])]; list[idx] = { ...proj, bullets: b }; update('projects', list); }} />
                  </div>
                </div>
              ))}
              <button onClick={() => update('projects', [...(data.projects || []), { title: '', role: '', dates: '', link: '', bullets: [''] }])} className="w-full border-2 border-dashed border-slate-200 rounded-lg py-3 text-[12px] font-semibold text-slate-400 hover:border-blue hover:text-blue transition-all flex items-center justify-center gap-1">
                <span className="msi text-[16px]">add</span> Add Project
              </button>
            </Section>
          )}

          {/* Leadership */}
          {(data.leadership?.length > 0) && (
            <Section title="Leadership & Projects" icon="groups" count={data.leadership?.length || 0} defaultOpen={false}>
              {(data.leadership || []).map((item, idx) => (
                <div key={idx} className="border border-slate-100 rounded-lg bg-slate-50/50 p-4 space-y-2 group">
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">Item {idx + 1}</span>
                    <button onClick={() => update('leadership', (data.leadership || []).filter((_, i) => i !== idx))} className="text-red-400 hover:text-red-600 transition-colors" title="Remove"><span className="msi text-[16px]">delete_outline</span></button>
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <label className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider">Title / Position</label>
                      <EditableText value={item.title || item.position} onChange={v => { const list = [...(data.leadership||[])]; list[idx] = { ...item, title: v }; update('leadership', list); }} placeholder="Title" />
                    </div>
                    <div>
                      <label className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider">Organization</label>
                      <EditableText value={item.organization} onChange={v => { const list = [...(data.leadership||[])]; list[idx] = { ...item, organization: v }; update('leadership', list); }} placeholder="Organization" />
                    </div>
                  </div>
                  <div>
                    <label className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider">Description</label>
                    <EditableText value={item.description} onChange={v => { const list = [...(data.leadership||[])]; list[idx] = { ...item, description: v }; update('leadership', list); }} placeholder="Description" multiline />
                  </div>
                  <div>
                    <label className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider mb-1 block">Bullet Points</label>
                    <BulletEditor bullets={item.bullets || item.responsibilities || []} onChange={b => { const list = [...(data.leadership||[])]; list[idx] = { ...item, bullets: b }; update('leadership', list); }} />
                  </div>
                </div>
              ))}
              <button onClick={() => update('leadership', [...(data.leadership || []), { title: '', organization: '', description: '', bullets: [''] }])} className="w-full border-2 border-dashed border-slate-200 rounded-lg py-3 text-[12px] font-semibold text-slate-400 hover:border-blue hover:text-blue transition-all flex items-center justify-center gap-1">
                <span className="msi text-[16px]">add</span> Add Item
              </button>
            </Section>
          )}

          {/* Education */}
          <Section title="Education" icon="school" count={data.education?.length || 0} defaultOpen={false}>
            {(data.education || []).map((edu, idx) => (
              <EducationCard key={idx} edu={edu} index={idx} onChange={e => updateEdu(idx, e)} onRemove={() => removeEdu(idx)} />
            ))}
            <button onClick={() => update('education', [...(data.education || []), { institution: '', degree: '', location: '', graduationDate: '', details: '' }])} className="w-full border-2 border-dashed border-slate-200 rounded-lg py-3 text-[12px] font-semibold text-slate-400 hover:border-blue hover:text-blue transition-all flex items-center justify-center gap-1">
              <span className="msi text-[16px]">add</span> Add Education
            </button>
          </Section>

          {/* Certifications */}
          {(data.certifications?.length > 0) && (
            <Section title="Certifications" icon="verified" count={data.certifications?.length || 0} defaultOpen={false}>
              {(data.certifications || []).map((cert, idx) => (
                <div key={idx} className="border border-slate-100 rounded-lg bg-slate-50/50 p-3 space-y-2 group">
                  <div className="flex items-center justify-between">
                    <EditableText value={cert.name || cert.title} onChange={v => updateCert(idx, { ...cert, name: v })} placeholder="Certification name" className="text-[12px] font-semibold" />
                    <button onClick={() => removeCert(idx)} className="opacity-0 group-hover:opacity-100 text-red-400 hover:text-red-600 transition-all"><span className="msi text-[14px]">close</span></button>
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <EditableText value={cert.issuer || cert.organization} onChange={v => updateCert(idx, { ...cert, issuer: v })} placeholder="Issuing org" className="text-[11px] text-slate-500" />
                    <EditableText value={cert.date || cert.year} onChange={v => updateCert(idx, { ...cert, date: v })} placeholder="Year" className="text-[11px] text-slate-500" />
                  </div>
                  <div>
                    <label className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider">Credential URL</label>
                    <EditableText value={cert.credentialUrl || cert.url || cert.link} onChange={v => updateCert(idx, { ...cert, credentialUrl: v })} placeholder="https://credential.link/…" className="text-[11px] text-blue-500" />
                  </div>
                </div>
              ))}
              <button onClick={() => update('certifications', [...(data.certifications || []), { name: '', issuer: '', date: '' }])} className="inline-flex items-center gap-1 text-[11px] font-semibold text-blue hover:text-blue/80 transition-colors">
                <span className="msi text-[14px]">add</span> Add certification
              </button>
            </Section>
          )}

          {/* Awards */}
          {(data.awards?.length > 0) && (
            <Section title="Awards & Recognition" icon="emoji_events" count={data.awards?.length || 0} defaultOpen={false}>
              {(data.awards || []).map((award, idx) => (
                <div key={idx} className="border border-slate-100 rounded-lg bg-slate-50/50 p-3 space-y-2 group">
                  <div className="flex items-center justify-between">
                    <EditableText value={award.title || award.name} onChange={v => updateAward(idx, { ...award, title: v })} placeholder="Award name" className="text-[12px] font-semibold" />
                    <button onClick={() => removeAward(idx)} className="opacity-0 group-hover:opacity-100 text-red-400 hover:text-red-600 transition-all"><span className="msi text-[14px]">close</span></button>
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <EditableText value={award.issuer || award.organization} onChange={v => updateAward(idx, { ...award, issuer: v })} placeholder="Issuing org" className="text-[11px] text-slate-500" />
                    <EditableText value={award.date || award.year} onChange={v => updateAward(idx, { ...award, date: v })} placeholder="Year" className="text-[11px] text-slate-500" />
                  </div>
                  <EditableText value={award.description} onChange={v => updateAward(idx, { ...award, description: v })} placeholder="Description" className="text-[11px] text-slate-600" />
                </div>
              ))}
            </Section>
          )}

          {/* Custom Sections */}
          {(data.customSections?.length > 0) && (
            <Section title="Custom Sections" icon="dashboard_customize" count={data.customSections?.length || 0} defaultOpen={false}>
              {(data.customSections || []).map((sec, idx) => (
                <div key={idx} className="border border-slate-200 rounded-lg p-4 space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">Section {idx + 1}</span>
                    <button onClick={() => removeCustom(idx)} className="text-red-400 hover:text-red-600 transition-colors"><span className="msi text-[14px]">delete_outline</span></button>
                  </div>
                  <CustomSectionEditor section={sec} onChange={s => updateCustom(idx, s)} />
                </div>
              ))}
            </Section>
          )}

          {/* Skills */}
          <Section title="Skills" icon="psychology" defaultOpen={false}>
            <SkillsEditor skills={data.skills} onChange={v => update('skills', v)} />
          </Section>

          {/* Interests */}
          <Section title="Interests" icon="interests" defaultOpen={false}>
            <EditableText value={data.interests} onChange={v => update('interests', v)} placeholder="Add your interests…" multiline className="text-[13px] text-slate-600" />
          </Section>

          <div className="pb-12" />
        </div>
      </div>
    </div>
  );
};

export default ResumeEditor;